import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  BanIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FileSpreadsheetIcon,
  HistoryIcon,
  MoreHorizontalIcon,
  PackageIcon,
  SendIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import LoadingSpinner from '@/components/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from '@/components/ui/combobox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import useUnidades from '@/hooks/useUnidades';
import { createRelatorioExcelReportStrategy } from '@/services/relatorioExcel/relatorioExcelReportFactory';
import {
  fetchHistoricoPublicacoes,
  fetchHistoricoVersoes,
  fetchRelatorioTransacoesAgrupado,
  fetchResumoProdutos,
  publicarAbate,
  publicarCompras,
  publicarNaoAbate,
  publicarProducaoMes,
  publicarVendas,
  relatorioTransacoesKeys,
} from '@/services/relatorioTransacoesService';
import { parseApiError } from '@/utils/apiError';
import { formatDateTime } from '@/utils/date';
import { naoAbateFormSchema, type TNaoAbateFormData } from './schema';
import type { IUnidade } from '@/types/unidade';
import type { IHistoricoPublicacao } from '@/types/publicacao';
import type {
  IHistoricoVersaoItem,
  IRelatorioTransacoesParams,
  ITransacaoAgrupada,
  TTransactionTipoParam,
} from '@/types/relatorioTransacoes';

export const VALID_TIPOS = new Set(['abate', 'producao', 'comercializacao']);

export function defaultAbateParams() {
  const now = new Date();
  return {
    tipo: 'abate' as const,
    ano: String(now.getFullYear()),
    mes: String(now.getMonth() + 1),
  };
}

export function normalizeTipoAno(params: { tipo: string; ano: string }) {
  const defaults = defaultAbateParams();
  return {
    tipo: VALID_TIPOS.has(params.tipo) ? params.tipo : defaults.tipo,
    ano: /^\d{4}$/.test(params.ano) ? params.ano : defaults.ano,
  };
}

export function normalizeMes(mes: string) {
  const mesNum = Number(mes);
  if (Number.isInteger(mesNum) && mesNum >= 1 && mesNum <= 12) return String(mesNum);
  return defaultAbateParams().mes;
}

type TRelatorioTransacoesPageProps = {
  tipo: string;
  ano: string;
  mes?: string;
};

const PAGE_SIZE = 15;

function toStr(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function normalizeMesKey(mes: unknown): string {
  if (mes == null) return '';
  const s = toStr(mes);
  if (s.length >= 7) return s.slice(0, 7);
  return s;
}

const formatarNumeroBR = (numero: unknown) =>
  Number(numero).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatarNumeroInteiro = (numero: unknown) => Number(numero).toLocaleString('pt-BR', { maximumFractionDigits: 0 });

const NUMERIC_TABLE_COLUMNS = new Set(['total_mes', 'peso_machos', 'quantidade_femeas', 'totalVendas', 'totalCompras']);

const formatarDataBR = (dataString: string | null | undefined): string => {
  if (!dataString) return '—';
  if (typeof dataString === 'string' && !dataString.includes('T') && !dataString.includes('Z')) {
    const [ano, mes, dia] = dataString.split('-');
    return new Date(Number(ano), Number(mes) - 1, Number(dia)).toLocaleDateString('pt-BR');
  }
  return new Date(dataString).toLocaleDateString('pt-BR');
};

const formatarMesAno = (mesAno: unknown): string => {
  const m = normalizeMesKey(mesAno);
  if (m.includes('-')) {
    const [ano, mes] = m.split('-');
    return `${mes}/${ano}`;
  }
  return toStr(mesAno);
};

const NECROPSIA_TIPO_LOTE = 'NECROPSIA';

const ABATE_NECROPSIA_DEDUPE_SUM_FIELDS = new Set(['lote_quantidade_machos', 'peso_morto_macho']);

function isNecropsiaLote(item: Record<string, unknown>): boolean {
  return toStr(item?.tipo_lote).trim().toUpperCase() === NECROPSIA_TIPO_LOTE;
}

function necropsiaDedupeKey(item: Record<string, unknown>): string {
  return [toStr(item?.nr_gta), toStr(item?.uf), toStr(item?.numero_lote)].join('|');
}

function sumFieldValue(
  items: Record<string, unknown>[] | null | undefined,
  field: string,
  { dedupeNecropsiaGroups = false }: { dedupeNecropsiaGroups?: boolean } = {},
): number {
  if (!items?.length || !field) return 0;
  const seenNecropsiaGroups = new Set<string>();
  return items.reduce((acc: number, item) => {
    if (dedupeNecropsiaGroups && isNecropsiaLote(item)) {
      const groupKey = necropsiaDedupeKey(item);
      if (seenNecropsiaGroups.has(groupKey)) return acc;
      seenNecropsiaGroups.add(groupKey);
    }
    const value = Number(item[field]);
    return acc + (Number.isFinite(value) ? value : 0);
  }, 0);
}

function sumAbateDetailFieldValue(items: Record<string, unknown>[], field: string): number {
  const dedupeNecropsiaGroups = ABATE_NECROPSIA_DEDUPE_SUM_FIELDS.has(field);
  return sumFieldValue(items, field, { dedupeNecropsiaGroups });
}

interface IColumnHelpers {
  normalizeMesKey: typeof normalizeMesKey;
  formatarNumeroBR: typeof formatarNumeroBR;
  formatarDataBR: typeof formatarDataBR;
  formatarMesAno: typeof formatarMesAno;
  dataPublicacaoFormatada: (row: Record<string, unknown>) => string;
}

interface IColumnDef {
  field: string;
  label: string;
  summable?: boolean;
  transformer?: (value: unknown, row: Record<string, unknown>, helpers: IColumnHelpers) => ReactNode;
}

const DEFAULT_TRANSACTION_COLUMNS: IColumnDef[] = [
  { field: 'tipo_transacao', label: 'Tipo', transformer: (value) => (value as ReactNode) || '—' },
  { field: 'mes', label: 'Período', transformer: (value) => formatarMesAno(value) },
  { field: 'unidade_nome', label: 'Unidade', transformer: (value, row) => (value as ReactNode) || (row?.nome_unidade as ReactNode) || '—' },
  { field: 'tipo', label: 'Categoria / grupo', transformer: (value) => (value as ReactNode) || '—' },
  { field: 'total_mes', label: 'Total (kg)', transformer: (value) => formatarNumeroBR(value) },
  {
    field: 'data_publicacao',
    label: 'Data publicação',
    transformer: (_value, row, helpers) => helpers.dataPublicacaoFormatada(row),
  },
];

const DEFAULT_DETAIL_COLUMNS: IColumnDef[] = [
  { field: 'cod_mapa', label: 'Código', transformer: (value) => (value as ReactNode) || '—' },
  { field: 'descricao', label: 'Descrição', transformer: (value) => (value as ReactNode) || '—' },
  { field: 'total', label: 'Total', summable: true, transformer: (value) => formatarNumeroBR(value) },
  { field: 'data_transacao', label: 'Data', transformer: (value) => formatarDataBR(value as string) },
];

const COMERCIALIZACAO_DETAIL_COLUMNS: IColumnDef[] = [
  { field: 'cod_mapa', label: 'Código', transformer: (value) => (value as ReactNode) || '—' },
  { field: 'descricao', label: 'Descrição', transformer: (value) => (value as ReactNode) || '—' },
  { field: 'ambito', label: 'Âmbito', transformer: (value) => (value as ReactNode) || '—' },
  { field: 'total', label: 'Total', summable: true, transformer: (value) => formatarNumeroBR(value) },
  { field: 'data_transacao', label: 'Data', transformer: (value) => formatarDataBR(value as string) },
];

interface ITransactionTab {
  key: string;
  label: string;
  transactionTypes: string[];
  columns: IColumnDef[];
  detailColumns: IColumnDef[];
}

const TRANSACTION_TABS: ITransactionTab[] = [
  {
    key: 'ABATE',
    label: 'Abate',
    transactionTypes: ['ABATE'],
    columns: [
      { field: 'tipo_transacao', label: 'Tipo', transformer: (value) => (value as ReactNode) || '—' },
      { field: 'data', label: 'Data', transformer: (value) => formatarDataBR(value as string) },
      { field: 'unidade_nome', label: 'Unidade', transformer: (value, row) => (value as ReactNode) || (row?.nome_unidade as ReactNode) || '—' },
      { field: 'tipo', label: 'Categoria / grupo', transformer: (value) => (value as ReactNode) || '—' },
      { field: 'total_mes', label: 'Total machos', transformer: (value) => formatarNumeroInteiro(value) },
      { field: 'quantidade_femeas', label: 'Total fêmea', transformer: (value) => formatarNumeroInteiro(value ?? 0) },
      {
        field: 'peso_machos',
        label: 'Peso total (kg)',
        transformer: (_value, row) => formatarNumeroBR((Number(row?.peso_machos) || 0) + (Number(row?.peso_femeas) || 0)),
      },
      {
        field: 'data_publicacao',
        label: 'Data publicação',
        transformer: (_value, row, helpers) => helpers.dataPublicacaoFormatada(row),
      },
    ],
    detailColumns: [
      { field: 'data_abate', label: 'Data', transformer: (value) => formatarDataBR(value as string) },
      { field: 'nr_gta', label: 'GTA', transformer: (value) => (value as ReactNode) || '—' },
      { field: 'uf', label: 'UF', transformer: (value) => (value as ReactNode) || '—' },
      { field: 'numero_lote', label: 'Lote', transformer: (value) => (value as ReactNode) || '—' },
      { field: 'tipo_lote', label: 'Tipo lote', transformer: (value) => (value as ReactNode) || '—' },
      { field: 'especie', label: 'Espécie', transformer: (value) => (value as ReactNode) || '—' },
      { field: 'lote_quantidade_machos', label: 'Machos', summable: true, transformer: (value) => (value as ReactNode) ?? '—' },
      { field: 'lote_quantidade_femeas', label: 'Fêmeas', summable: true, transformer: (value) => (value as ReactNode) ?? '—' },
      { field: 'peso_morto_macho', label: 'Peso macho', summable: true, transformer: (value) => formatarNumeroBR(value || 0) },
      { field: 'peso_morto_femea', label: 'Peso fêmea', summable: true, transformer: (value) => formatarNumeroBR(value || 0) },
      { field: 'diagnostico_nome', label: 'Diagnóstico', transformer: (value) => (value as ReactNode) || '—' },
      { field: 'parte_afetada_nome', label: 'Parte afetada', transformer: (value) => (value as ReactNode) || '—' },
      { field: 'quantidade_partes_afetadas', label: 'Qtd. partes', summable: true, transformer: (value) => (value as ReactNode) ?? '—' },
      { field: 'destino_condenacao', label: 'Destino', transformer: (value) => (value as ReactNode) || '—' },
    ],
  },
  {
    key: 'PRODUCAO',
    label: 'Produção',
    transactionTypes: ['PRODUCAO'],
    columns: [
      { field: 'tipo_transacao', label: 'Tipo', transformer: (value) => (value as ReactNode) || '—' },
      { field: 'mes', label: 'Período', transformer: (value) => formatarMesAno(value) },
      { field: 'unidade_nome', label: 'Unidade', transformer: (value, row) => (value as ReactNode) || (row?.nome_unidade as ReactNode) || '—' },
      { field: 'total_mes', label: 'Total (kg)', transformer: (value) => formatarNumeroBR(value) },
      {
        field: 'data_publicacao',
        label: 'Data publicação',
        transformer: (_value, row, helpers) => helpers.dataPublicacaoFormatada(row),
      },
    ],
    detailColumns: DEFAULT_DETAIL_COLUMNS,
  },
  {
    key: 'COMERCIALIZACAO',
    label: 'Comercialização',
    transactionTypes: ['COMERCIALIZACAO'],
    columns: [
      { field: 'mes', label: 'Período', transformer: (value) => formatarMesAno(value) },
      { field: 'unidade_nome', label: 'Unidade', transformer: (value, row) => (value as ReactNode) || (row?.nome_unidade as ReactNode) || '—' },
      { field: 'totalVendas', label: 'Total vendas (kg)', transformer: (value) => formatarNumeroBR(value ?? 0) },
      { field: 'totalCompras', label: 'Total compras (kg)', transformer: (value) => formatarNumeroBR(value ?? 0) },
      {
        field: 'data_publicacao',
        label: 'Data publicação',
        transformer: (_value, row, helpers) => helpers.dataPublicacaoFormatada(row),
      },
    ],
    detailColumns: DEFAULT_DETAIL_COLUMNS,
  },
];

/** Funde as listagens de VENDA + COMPRA (mesmo header `transacao_comercializacao`) numa linha por id. */
function mergeComercializacao(vendaRows: ITransacaoAgrupada[], compraRows: ITransacaoAgrupada[]): ITransacaoAgrupada[] {
  const merged = new Map<string, ITransacaoAgrupada>();

  const ensure = (base: ITransacaoAgrupada): ITransacaoAgrupada => {
    const key = String(base.id);
    let row = merged.get(key);
    if (!row) {
      row = {
        id: base.id,
        mes: base.mes,
        data: base.data,
        tipo: '—',
        tipo_transacao: 'COMERCIALIZACAO',
        unidade: base.unidade,
        unidade_nome: base.unidade_nome,
        nome_unidade: base.nome_unidade,
        total_mes: 0,
        publicado: base.publicado,
        data_publicacao: base.data_publicacao,
        detalhes: [],
        totalVendas: 0,
        totalCompras: 0,
        detalhesVendas: [],
        detalhesCompras: [],
      };
      merged.set(key, row);
    }
    return row;
  };

  vendaRows.forEach((row) => {
    const item = ensure(row);
    item.totalVendas = row.total_mes;
    item.detalhesVendas = row.detalhes;
  });
  compraRows.forEach((row) => {
    const item = ensure(row);
    item.totalCompras = row.total_mes;
    item.detalhesCompras = row.detalhes;
  });

  return Array.from(merged.values());
}

const TAB_TO_TIPO_PARAM: Record<string, string> = {
  ABATE: 'abate',
  PRODUCAO: 'producao',
  COMERCIALIZACAO: 'comercializacao',
};

const TIPO_PARAM_TO_TAB: Record<string, string> = {
  abate: 'ABATE',
  producao: 'PRODUCAO',
  comercializacao: 'COMERCIALIZACAO',
};

const MONTH_OPTIONS = [
  { value: '1', label: 'Janeiro' },
  { value: '2', label: 'Fevereiro' },
  { value: '3', label: 'Março' },
  { value: '4', label: 'Abril' },
  { value: '5', label: 'Maio' },
  { value: '6', label: 'Junho' },
  { value: '7', label: 'Julho' },
  { value: '8', label: 'Agosto' },
  { value: '9', label: 'Setembro' },
  { value: '10', label: 'Outubro' },
  { value: '11', label: 'Novembro' },
  { value: '12', label: 'Dezembro' },
];

function historicoSucesso(pub: IHistoricoPublicacao): boolean {
  const st = (pub.status || '').toUpperCase();
  return st === 'SUCESSO' || pub.sucesso === true;
}

function mesPublicacao(pub: IHistoricoPublicacao): string {
  const m = pub.mes;
  if (m != null && String(m).length >= 7) return String(m).slice(0, 7);
  if (m != null && /^\d{1,2}$/.test(String(m)) && pub.data_inicio) {
    return String(pub.data_inicio).slice(0, 7);
  }
  if (pub.data_inicio) return String(pub.data_inicio).slice(0, 7);
  return '';
}

function normalizeDataKey(data: unknown): string {
  if (data == null || data === '') return '';
  const s = toStr(data);
  if (s.length >= 10) return s.slice(0, 10);
  return s;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function buildMissingAbateRows(existingAbateRows: ITransacaoAgrupada[], year: number, month: string): ITransacaoAgrupada[] {
  const monthNum = Number(month);
  const yearNum = Number(year);
  if (!monthNum || !yearNum) return [];

  const lastDay = daysInMonth(yearNum, monthNum);
  const mesKey = `${yearNum}-${String(monthNum).padStart(2, '0')}`;
  const existingDates = new Set(existingAbateRows.map((row) => normalizeDataKey(row.data)).filter(Boolean));

  const missing: ITransacaoAgrupada[] = [];
  for (let day = 1; day <= lastDay; day += 1) {
    const dateStr = `${mesKey}-${String(day).padStart(2, '0')}`;
    if (existingDates.has(dateStr)) continue;
    missing.push({
      id: null,
      tipo_transacao: 'ABATE',
      data: dateStr,
      mes: mesKey,
      tipo: '—',
      unidade: '',
      total_mes: 0,
      peso_machos: 0,
      quantidade_femeas: 0,
      peso_femeas: 0,
      detalhes: [],
      sem_abate: true,
    });
  }
  return missing;
}

function SubTabela({
  rows,
  columns,
  helpers,
}: {
  rows: Record<string, unknown>[];
  columns: IColumnDef[];
  helpers: IColumnHelpers;
}) {
  if (!rows.length) {
    return <p className="text-sm text-on-surface-variant">Nenhum item.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-outline-variant">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead key={column.field}>{column.label}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={index}>
              {columns.map((column) => {
                const value = row[column.field];
                const rendered = typeof column.transformer === 'function' ? column.transformer(value, row, helpers) : (value as ReactNode);
                return <TableCell key={column.field}>{rendered ?? '—'}</TableCell>;
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function RelatorioTransacoesPage({ tipo, ano, mes }: TRelatorioTransacoesPageProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const selectedYear = Number(ano);
  const selectedMonth = mes ?? defaultAbateParams().mes;
  const selectedTransactionTab = TIPO_PARAM_TO_TAB[tipo] ?? 'ABATE';

  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const toggleExpandedRow = (key: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const navigateFilters = (next: { tipo?: string; ano?: string; mes?: string }) => {
    const nextTipo = next.tipo ?? tipo;
    const nextAno = next.ano ?? ano;
    if (nextTipo === 'abate') {
      void navigate({
        to: '/relatorio-transacoes/$tipo/$ano/$mes',
        params: {
          tipo: 'abate',
          ano: nextAno,
          mes: next.mes ?? selectedMonth,
        },
      });
      return;
    }
    void navigate({
      to: '/relatorio-transacoes/$tipo/$ano',
      params: {
        tipo: nextTipo,
        ano: nextAno,
      },
    });
  };

  const [modalDetalhes, setModalDetalhes] = useState<Record<string, unknown>[]>([]);
  const [showModalDetalhes, setShowModalDetalhes] = useState(false);
  const [modalDetalhesLoading, setModalDetalhesLoading] = useState(false);
  const [modalDetalhesTabKey, setModalDetalhesTabKey] = useState('ABATE');

  const [modalHistorico, setModalHistorico] = useState<IHistoricoVersaoItem[]>([]);
  const [showModalHistorico, setShowModalHistorico] = useState(false);
  const [modalHistoricoLoading, setModalHistoricoLoading] = useState(false);
  const [modalHistoricoItem, setModalHistoricoItem] = useState<ITransacaoAgrupada | null>(null);
  // ABATE tem colunas próprias (machos/fêmea/peso); PRODUCAO e COMERCIALIZACAO usam o
  // mesmo formato simples de "Total (kg)".
  const isProducaoHistorico = modalHistoricoItem?.tipo_transacao !== 'ABATE';
  const showOrigemColumn = modalHistoricoItem?.tipo_transacao === 'COMERCIALIZACAO';

  const [publishingStatus, setPublishingStatus] = useState<Record<string, string | null>>({});

  const [loadingExcel, setLoadingExcel] = useState(false);

  const [showModalNaoAbate, setShowModalNaoAbate] = useState(false);
  const [publishConfirmCtx, setPublishConfirmCtx] = useState<{ item: ITransacaoAgrupada; mesN: string } | null>(null);
  const [naoAbateData, setNaoAbateData] = useState('');
  const [naoAbateComboboxPortalContainer, setNaoAbateComboboxPortalContainer] = useState<HTMLElement | null>(null);

  const naoAbateForm = useForm<TNaoAbateFormData>({
    resolver: zodResolver(naoAbateFormSchema),
    defaultValues: { unidade_id: '', justificativa: '' },
  });
  const {
    control: naoAbateControl,
    register: registerNaoAbate,
    handleSubmit: handleNaoAbateSubmit,
    reset: resetNaoAbateForm,
    setValue: setNaoAbateValue,
    getValues: getNaoAbateValues,
    formState: { errors: naoAbateErrors },
  } = naoAbateForm;

  const {
    data: naoAbateUnidades,
    loading: naoAbateUnidadesLoading,
    error: naoAbateUnidadesError,
  } = useUnidades({ skip: 0, limit: 100, enabled: showModalNaoAbate });

  const singleNaoAbateUnidadeLocked = naoAbateUnidades.length === 1 && !naoAbateUnidadesLoading && !naoAbateUnidadesError;

  useEffect(() => {
    if (!showModalNaoAbate || !singleNaoAbateUnidadeLocked) return;
    const onlyId = String(naoAbateUnidades[0].id);
    if (getNaoAbateValues('unidade_id') !== onlyId) {
      setNaoAbateValue('unidade_id', onlyId, { shouldValidate: true });
    }
  }, [showModalNaoAbate, singleNaoAbateUnidadeLocked, naoAbateUnidades, getNaoAbateValues, setNaoAbateValue]);

  const publicacoesQuery = useQuery({
    queryKey: ['publicacoes-realizadas', selectedYear],
    queryFn: async () => {
      const result = await fetchHistoricoPublicacoes();
      return (result.items || []).filter(historicoSucesso);
    },
  });
  const publicacoesRealizadas = useMemo(() => publicacoesQuery.data ?? [], [publicacoesQuery.data]);

  const isComercializacao = selectedTransactionTab === 'COMERCIALIZACAO';

  const relatorioParams: IRelatorioTransacoesParams = {
    ano: String(selectedYear),
    tipo: TAB_TO_TIPO_PARAM[selectedTransactionTab] as TTransactionTipoParam,
    mes: selectedTransactionTab === 'ABATE' ? selectedMonth : undefined,
  };
  const vendaParams: IRelatorioTransacoesParams = { ano: String(selectedYear), tipo: 'expedicao' };
  const compraParams: IRelatorioTransacoesParams = { ano: String(selectedYear), tipo: 'recebimento' };

  const outrosQuery = useQuery({
    queryKey: relatorioTransacoesKeys.agrupado(relatorioParams),
    queryFn: () => fetchRelatorioTransacoesAgrupado(relatorioParams),
    enabled: !isComercializacao,
  });
  const vendaQuery = useQuery({
    queryKey: relatorioTransacoesKeys.agrupado(vendaParams),
    queryFn: () => fetchRelatorioTransacoesAgrupado(vendaParams),
    enabled: isComercializacao,
  });
  const compraQuery = useQuery({
    queryKey: relatorioTransacoesKeys.agrupado(compraParams),
    queryFn: () => fetchRelatorioTransacoesAgrupado(compraParams),
    enabled: isComercializacao,
  });

  const loading = isComercializacao ? vendaQuery.isLoading || compraQuery.isLoading : outrosQuery.isLoading;
  const isError = isComercializacao ? vendaQuery.isError || compraQuery.isError : outrosQuery.isError;
  const relatorioError = isComercializacao ? (vendaQuery.error ?? compraQuery.error) : outrosQuery.error;
  const fetchData = () => {
    if (isComercializacao) {
      void vendaQuery.refetch();
      void compraQuery.refetch();
      return;
    }
    void outrosQuery.refetch();
  };
  const allRows = useMemo(() => {
    if (isComercializacao) {
      return mergeComercializacao(vendaQuery.data?.resultados ?? [], compraQuery.data?.resultados ?? []);
    }
    return outrosQuery.data?.resultados ?? [];
  }, [isComercializacao, vendaQuery.data, compraQuery.data, outrosQuery.data]);
  const error = isError ? parseApiError(relatorioError, 'Erro ao carregar relatório') : null;

  const [previousAllRows, setPreviousAllRows] = useState(allRows);
  if (allRows !== previousAllRows) {
    setPreviousAllRows(allRows);
    setCurrentPage(1);
  }

  const publicacaoMatch = useMemo(() => {
    return (tipoTransacao: string, mes: string, unidadeId: string | null = null): IHistoricoPublicacao | undefined => {
      const m = normalizeMesKey(mes);
      const tipo = (tipoTransacao || '').toUpperCase();
      const unidade = unidadeId != null && unidadeId !== '' ? unidadeId : null;
      return publicacoesRealizadas.find((pub) => {
        const pubTipo = (pub.tipo_publicacao || '').toUpperCase();
        const tipoOk = pubTipo.includes(tipo) || tipo.includes(pubTipo) || pubTipo === tipo;
        if (!tipoOk || mesPublicacao(pub) !== m) return false;
        if (unidade == null) return true;
        const pubUnidade = pub.unidade_id;
        return pubUnidade != null && pubUnidade === unidade;
      });
    };
  }, [publicacoesRealizadas]);

  const jaFoiPublicado = (tipoTransacao: string, mes: string, row: ITransacaoAgrupada | null = null): boolean => {
    const tipo = (tipoTransacao || '').toUpperCase();
    if ((tipo === 'ABATE' || tipo === 'PRODUCAO' || tipo === 'COMERCIALIZACAO') && row) {
      return row.publicado === true;
    }
    return !!publicacaoMatch(tipoTransacao, mes, row?.unidade ?? null);
  };

  const dataPublicacaoFormatada = (row: Record<string, unknown>): string => {
    const tipo = toStr(row?.tipo_transacao).toUpperCase();
    const dataPublicacao =
      tipo === 'ABATE' || tipo === 'PRODUCAO' || tipo === 'COMERCIALIZACAO'
        ? (row?.data_publicacao as string | null | undefined)
        : (() => {
            const p = publicacaoMatch(row.tipo_transacao as string, normalizeMesKey(row.mes), row?.unidade as string);
            return p ? p.data_publicacao || p.data_fim || p.data_inicio : null;
          })();
    if (!dataPublicacao) return '—';
    try {
      return new Date(dataPublicacao).toLocaleString('pt-BR');
    } catch {
      return String(dataPublicacao);
    }
  };

  const activeTransactionTab = useMemo(
    () => TRANSACTION_TABS.find((tab) => tab.key === selectedTransactionTab) || TRANSACTION_TABS[0],
    [selectedTransactionTab],
  );

  const activeColumns = activeTransactionTab.columns || DEFAULT_TRANSACTION_COLUMNS;
  const activeDetailColumns = activeTransactionTab.detailColumns || DEFAULT_DETAIL_COLUMNS;

  const filteredRows = useMemo(() => {
    const rows = allRows;

    if (selectedTransactionTab !== 'ABATE' || !selectedMonth) {
      return rows;
    }

    const missingRows = buildMissingAbateRows(rows, selectedYear, selectedMonth);
    if (!missingRows.length) return rows;

    return [...rows, ...missingRows].sort((a, b) => normalizeDataKey(a.data).localeCompare(normalizeDataKey(b.data)));
  }, [allRows, selectedTransactionTab, selectedYear, selectedMonth]);

  const totalItems = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  const data = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, currentPage]);

  const handleTransactionTabChange = (tabKey: string) => {
    if (tabKey === selectedTransactionTab) return;
    navigateFilters({ tipo: TAB_TO_TIPO_PARAM[tabKey] });
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [tipo, ano, mes]);

  const rowKey = (item: ITransacaoAgrupada): string =>
    `${item.tipo_transacao}-${normalizeDataKey(item.data || item.mes)}-${item.tipo}-${item.sem_abate ? 'sem-abate' : item.id}`;

  const openNaoAbateModal = (item: ITransacaoAgrupada) => {
    setNaoAbateData(normalizeDataKey(item.data));
    resetNaoAbateForm({ unidade_id: '', justificativa: '' });
    setShowModalNaoAbate(true);
  };

  const closeNaoAbateModal = () => {
    setShowModalNaoAbate(false);
    setNaoAbateData('');
    resetNaoAbateForm({ unidade_id: '', justificativa: '' });
  };

  const closePublishConfirmModal = () => setPublishConfirmCtx(null);

  const naoAbateMutation = useMutation({ mutationFn: publicarNaoAbate });

  const handleSaveNaoAbate = async (formData: TNaoAbateFormData) => {
    const unidadeEncontrada = naoAbateUnidades.find((unidade) => String(unidade.id) === formData.unidade_id);
    if (!unidadeEncontrada) {
      toast.error('Erro ao encontrar a unidade.');
      return;
    }

    try {
      await naoAbateMutation.mutateAsync({
        estabelecimento: {
          numero: unidadeEncontrada.mapa_estabelecimento_numero,
          ambito: unidadeEncontrada.mapa_estabelecimento_ambito,
          cpf_Cnpj: unidadeEncontrada.mapa_estabelecimento_cnpj,
          cod_uf: unidadeEncontrada.mapa_estabelecimento_uf,
          cod_municipio_ibge: unidadeEncontrada.mapa_estabelecimento_municipio_ibge,
        },
        dataAbate: naoAbateData,
        abate: 'N',
        observacao: formData.justificativa,
        saldo: [],
        unidade_id: unidadeEncontrada.id,
      });

      toast.success(`Publicação de não abate para ${formatarDataBR(naoAbateData)} registrada.`);
      closeNaoAbateModal();
    } catch (err) {
      toast.error(parseApiError(err, 'Falha ao publicar não abate.'));
    }
  };

  const openDetalhes = async (item: ITransacaoAgrupada) => {
    setModalDetalhesTabKey(selectedTransactionTab);
    setShowModalDetalhes(true);
    if (selectedTransactionTab !== 'ABATE') {
      setModalDetalhes((item.detalhes as unknown as Record<string, unknown>[]) || []);
      return;
    }
    if (item.id == null) {
      setModalDetalhes([]);
      toast.error('ID do abate não encontrado para carregar detalhes.');
      return;
    }
    setModalDetalhesLoading(true);
    setModalDetalhes([]);
    try {
      const result = await fetchResumoProdutos('abate', String(item.id));
      setModalDetalhes(Array.isArray(result) ? result : []);
    } catch (err) {
      setModalDetalhes([]);
      toast.error(parseApiError(err, 'Falha ao carregar detalhes do abate.'));
    } finally {
      setModalDetalhesLoading(false);
    }
  };

  const openHistorico = async (item: ITransacaoAgrupada) => {
    const params = item.id != null ? { id: String(item.id) } : item.data ? { data: String(item.data) } : null;
    if (!params) {
      toast.error('Data não encontrada para carregar histórico.');
      return;
    }
    setModalHistoricoLoading(true);
    try {
      if (selectedTransactionTab === 'COMERCIALIZACAO') {
        const [vendaVersoes, compraVersoes] = await Promise.all([
          fetchHistoricoVersoes('expedicao', params),
          fetchHistoricoVersoes('recebimento', params),
        ]);
        const versoes = [
          ...(Array.isArray(vendaVersoes) ? vendaVersoes : []).map((v) => ({ ...v, origem: 'Venda' })),
          ...(Array.isArray(compraVersoes) ? compraVersoes : []).map((v) => ({ ...v, origem: 'Compra' })),
        ];
        if (versoes.length === 0) {
          toast.info('Não há histórico para este registro.');
          return;
        }
        setModalHistoricoItem(item);
        setModalHistorico(versoes);
        setShowModalHistorico(true);
        return;
      }

      const tipoParam = TAB_TO_TIPO_PARAM[selectedTransactionTab] ?? 'abate';
      const result = await fetchHistoricoVersoes(tipoParam as TTransactionTipoParam, params);
      const versoes = Array.isArray(result) ? result : [];
      if (versoes.length === 0) {
        toast.info('Não há histórico para este registro.');
        return;
      }
      setModalHistoricoItem(item);
      setModalHistorico(versoes);
      setShowModalHistorico(true);
    } catch (err) {
      toast.error(parseApiError(err, 'Falha ao carregar histórico de versões.'));
    } finally {
      setModalHistoricoLoading(false);
    }
  };

  function finishPublishSuccess(tipoTransacao: string, mes: string, extra: string | null) {
    const m = normalizeMesKey(mes);
    setPublishingStatus((prev) => ({ ...prev, [`${tipoTransacao}-${m}`]: 'success' }));
    const periodo = tipoTransacao === 'ABATE' ? formatarDataBR(mes) : formatarMesAno(m);
    const msg = extra ? `${tipoTransacao} ${periodo} publicada. (${extra})` : `${tipoTransacao} ${periodo} publicada.`;
    toast.success(msg);
    setTimeout(() => {
      void fetchData();
      void queryClient.invalidateQueries({ queryKey: ['publicacoes-realizadas'] });
      setPublishingStatus((prev) => ({ ...prev, [`${tipoTransacao}-${m}`]: null }));
    }, 1500);
  }

  const handlePublish = async (item: ITransacaoAgrupada, mesN: string) => {
    const tipoTransacao = item.tipo_transacao;
    const id = item.id ?? null;
    const mes = item.tipo_transacao === 'ABATE' ? item.data : mesN;

    const mesKey = normalizeMesKey(mes);
    toast.info('Iniciando publicação');
    try {
      setPublishingStatus((prev) => ({ ...prev, [`${tipoTransacao}-${mesKey}`]: 'publishing' }));

      if (tipoTransacao === 'ABATE') {
        if (id == null) {
          throw new Error('ID do abate não encontrado para publicação.');
        }
        await publicarAbate(String(id));
        finishPublishSuccess(tipoTransacao, mes ?? '', '1 registro');
        return;
      }

      if (tipoTransacao === 'PRODUCAO') {
        const m = normalizeMesKey(mes);
        await publicarProducaoMes(m, item.unidade);
      } else if (tipoTransacao === 'VENDA' || tipoTransacao === 'COMPRA' || tipoTransacao === 'COMERCIALIZACAO') {
        const m = normalizeMesKey(mes);
        const [ano, mesNum] = m.split('-');
        const dataInicio = `${ano}-${mesNum}-01`;
        const dataFim = `${ano}-${mesNum}-${new Date(Number(ano), Number(mesNum), 0).getDate()}`;
        if (tipoTransacao === 'COMPRA') {
          await publicarCompras(dataInicio, dataFim, String(item.unidade));
        } else {
          // 'VENDA' ou 'COMERCIALIZACAO' — backend publica venda+compra juntas numa chamada só.
          await publicarVendas(dataInicio, dataFim, String(item.unidade));
        }
      } else {
        throw new Error(`Tipo de transação desconhecido: ${tipoTransacao}`);
      }

      finishPublishSuccess(tipoTransacao, mesKey, null);
    } catch (err) {
      setPublishingStatus((prev) => ({ ...prev, [`${tipoTransacao}-${mesKey}`]: 'error' }));
      toast.error(`Erro ao publicar ${tipoTransacao} de ${formatarMesAno(mesKey)}: ${parseApiError(err)}`);
      setTimeout(() => {
        setPublishingStatus((prev) => ({ ...prev, [`${tipoTransacao}-${mesKey}`]: null }));
      }, 4000);
    }
  };

  const onPublishClick = (item: ITransacaoAgrupada, mesN: string) => {
    if (selectedTransactionTab === 'COMERCIALIZACAO') {
      setPublishConfirmCtx({ item, mesN });
      return;
    }
    void handlePublish(item, mesN);
  };

  const confirmPublish = () => {
    if (!publishConfirmCtx) return;
    const { item, mesN } = publishConfirmCtx;
    closePublishConfirmModal();
    void handlePublish(item, mesN);
  };

  const getStatusLabel = (tipoTransacao: string, mes: string, categoriaGrupo: string | null = null, row: ITransacaoAgrupada | null = null) => {
    const k = `${tipoTransacao}-${normalizeMesKey(mes)}`;
    const st = publishingStatus[k];
    const cat = String(categoriaGrupo ?? '').trim();
    if (jaFoiPublicado(tipoTransacao, mes, row)) return { text: 'Publicado', className: 'text-on-surface-variant' };
    if (tipoTransacao === 'PRODUCAO' && cat === 'SEM_VINCULO_MAPA') return { text: 'Sem vínculo MAPA', className: 'text-error' };
    if (st === 'publishing') return { text: 'Publicando…', className: 'text-primary' };
    if (st === 'success') return { text: 'Concluído', className: 'text-success' };
    if (st === 'error') return { text: 'Erro', className: 'text-error' };
    return { text: 'Publicar', className: 'text-on-surface' };
  };

  const isPublishDisabled = (tipoTransacao: string, mes: string, categoriaGrupo: string | null = null, row: ITransacaoAgrupada | null = null) => {
    const k = `${tipoTransacao}-${normalizeMesKey(mes)}`;
    const st = publishingStatus[k];
    const cat = String(categoriaGrupo ?? '').trim();
    if (tipoTransacao === 'PRODUCAO' && cat === 'SEM_VINCULO_MAPA') return true;
    return jaFoiPublicado(tipoTransacao, mes, row) || st === 'publishing' || st === 'success';
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const columnHelpers: IColumnHelpers = {
    normalizeMesKey,
    formatarNumeroBR,
    formatarDataBR,
    formatarMesAno,
    dataPublicacaoFormatada,
  };

  const renderColumnValue = (item: Record<string, unknown>, column: IColumnDef): ReactNode => {
    const value = item[column.field];
    const rendered = typeof column.transformer === 'function' ? column.transformer(value, item, columnHelpers) : (value as ReactNode);
    return rendered ?? '—';
  };

  const renderDetailValue = (item: Record<string, unknown>, column: IColumnDef): ReactNode => {
    const value = item[column.field];
    const rendered = typeof column.transformer === 'function' ? column.transformer(value, item, columnHelpers) : (value as ReactNode);
    return rendered ?? '—';
  };

  const renderDetailTotalValue = (column: IColumnDef, colIndex: number): ReactNode => {
    if (colIndex === 0) return 'TOTAL';
    if (!column.summable) return '—';
    const total = modalDetalhesTabKey === 'ABATE' ? sumAbateDetailFieldValue(modalDetalhes, column.field) : sumFieldValue(modalDetalhes, column.field);
    if (typeof column.transformer === 'function') {
      return column.transformer(total, {}, columnHelpers) ?? '—';
    }
    return total;
  };

  const modalDetailColumns = TRANSACTION_TABS.find((tab) => tab.key === modalDetalhesTabKey)?.detailColumns || activeDetailColumns;

  const onExcelButtonClick = async (item: ITransacaoAgrupada) => {
    try {
      setLoadingExcel(true);

      const strategy = createRelatorioExcelReportStrategy(selectedTransactionTab, {
        formatarDataBR,
        normalizeMesKey,
      });

      if (!strategy) return;

      const result = await strategy.build(item);

      if (result.empty || !result.workbook || !result.fileName) {
        toast.error('Não há dados para exportar');
        return;
      }

      XLSX.writeFile(result.workbook, result.fileName);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingExcel(false);
    }
  };

  if (loading && totalItems === 0) {
    return (
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-2xl font-semibold text-on-surface">Relatório de transações</h1>
          <p className="text-on-surface-variant">Agrupamento por período e categoria</p>
        </div>
        <LoadingSpinner text="Carregando relatório…" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold text-on-surface">Relatório de transações</h1>
        <p className="text-on-surface-variant">Agrupamento por período.</p>
      </div>

      <div className="rounded-lg border border-outline-variant p-4">
        <h2 className="mb-4 text-lg font-semibold text-on-surface">Filtros</h2>
        <div className="flex items-end gap-4">
          <Field className="w-32">
            <FieldLabel htmlFor="year">Ano</FieldLabel>
            <Select
              value={String(selectedYear)}
              onValueChange={(value) => navigateFilters({ ano: value })}
            >
              <SelectTrigger
                id="year"
                className="w-full"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return (
                      <SelectItem
                        key={year}
                        value={String(year)}
                      >
                        {year}
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              </SelectContent>
            </Select>
          </Field>
          <Button
            onClick={() => void fetchData()}
            disabled={loading}
          >
            Atualizar
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-outline-variant p-4">
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-on-surface">Transações</h2>
          {totalItems > 0 && (
            <span className="text-sm text-on-surface-variant">
              {totalItems} grupo(s) — página {currentPage}/{totalPages}
            </span>
          )}
        </div>

        <Tabs
          value={selectedTransactionTab}
          onValueChange={handleTransactionTabChange}
          className="mb-4"
        >
          <TabsList>
            {TRANSACTION_TABS.map((tab) => (
              <TabsTrigger
                key={tab.key}
                value={tab.key}
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {selectedTransactionTab === 'ABATE' && (
          <Field className="mb-4 w-40">
            <FieldLabel htmlFor="month">Mês</FieldLabel>
            <Select
              value={selectedMonth}
              onValueChange={(value) => navigateFilters({ mes: value })}
            >
              <SelectTrigger
                id="month"
                className="w-full"
              >
                <SelectValue placeholder="Selecione o mês…" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {MONTH_OPTIONS.map((opt) => (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </Field>
        )}

        {error && (
          <div
            role="alert"
            className="mb-4 rounded-lg bg-error-container p-4 text-on-error-container"
          >
            {error}
          </div>
        )}
        {loading ? (
          <LoadingSpinner
            text="Carregando…"
            size="small"
          />
        ) : totalItems === 0 ? (
          <p className="p-8 text-center text-on-surface-variant">
            Nenhuma transação de {activeTransactionTab.label} para{' '}
            {selectedTransactionTab === 'ABATE' ? `${MONTH_OPTIONS.find((m) => m.value === selectedMonth)?.label || selectedMonth}/${selectedYear}` : selectedYear}.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-outline-variant">
              <Table>
                <TableHeader>
                  <TableRow>
                    {isComercializacao && <TableHead className="w-8" />}
                    {activeColumns.map((column) => (
                      <TableHead
                        key={column.field}
                        className={NUMERIC_TABLE_COLUMNS.has(column.field) ? 'text-right' : ''}
                      >
                        {column.label}
                      </TableHead>
                    ))}
                    <TableHead className="w-12">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((item) => {
                    const k = rowKey(item);
                    const mesN = normalizeMesKey(item.mes);
                    // ponytail: restore `const pub = jaFoiPublicado(...)` when re-enabling Atualizar no MAPA
                    const sl = getStatusLabel(item.tipo_transacao, mesN, item.tipo, item);
                    const isExpanded = expandedRows.has(k);
                    return (
                      <Fragment key={k}>
                      <TableRow
                        className={item.sem_abate ? 'bg-surface-container/50' : ''}
                      >
                          {isComercializacao && (
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label={isExpanded ? 'Recolher' : 'Expandir'}
                                onClick={() => toggleExpandedRow(k)}
                              >
                                {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                              </Button>
                            </TableCell>
                          )}
                          {activeColumns.map((column) => (
                            <TableCell
                              key={`${k}-${column.field}`}
                              className={NUMERIC_TABLE_COLUMNS.has(column.field) ? 'text-right tabular-nums' : ''}
                            >
                              {renderColumnValue(item as unknown as Record<string, unknown>, column)}
                            </TableCell>
                          ))}
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  aria-label="Ações"
                                >
                                  <MoreHorizontalIcon />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent
                                align="end"
                                className="w-auto min-w-48"
                              >
                                <DropdownMenuGroup>
                                  {selectedTransactionTab !== 'COMERCIALIZACAO' && (
                                    <DropdownMenuItem
                                      disabled={item.sem_abate}
                                      title={item.sem_abate ? 'Sem abate neste dia.' : undefined}
                                      onClick={() => void openDetalhes(item)}
                                    >
                                      <PackageIcon />
                                      Resumo produtos
                                    </DropdownMenuItem>
                                  )}
                                  {(selectedTransactionTab === 'ABATE' || selectedTransactionTab === 'COMERCIALIZACAO') && (
                                    <DropdownMenuItem
                                      disabled={loadingExcel || item.sem_abate}
                                      title={item.sem_abate ? 'Sem abate neste dia.' : undefined}
                                      onClick={() => void onExcelButtonClick(item)}
                                    >
                                      <FileSpreadsheetIcon />
                                      Excel
                                    </DropdownMenuItem>
                                  )}
                                  {(selectedTransactionTab === 'ABATE' ||
                                    selectedTransactionTab === 'PRODUCAO' ||
                                    selectedTransactionTab === 'COMERCIALIZACAO') && (
                                    <DropdownMenuItem
                                      onClick={() => void openHistorico(item)}
                                    >
                                      <HistoryIcon />
                                      Histórico
                                    </DropdownMenuItem>
                                  )}
                                  {/* ponytail: re-enable Atualizar no MAPA — restore Link/RefreshCwIcon imports + `const pub = jaFoiPublicado(...)`
                                  {pub && (
                                    <DropdownMenuItem asChild>
                                      <Link to="/atualizar-transacoes">
                                        <RefreshCwIcon />
                                        Atualizar no MAPA
                                      </Link>
                                    </DropdownMenuItem>
                                  )}
                                  */}
                                  {item.sem_abate && (
                                    <DropdownMenuItem onClick={() => openNaoAbateModal(item)}>
                                      <BanIcon />
                                      Publicar não Abate
                                    </DropdownMenuItem>
                                  )}
                                  {!item.sem_abate && (
                                    <DropdownMenuItem
                                      disabled={isPublishDisabled(item.tipo_transacao, mesN, item.tipo, item)}
                                      title={
                                        item.tipo_transacao === 'PRODUCAO' && String(item.tipo || '').trim() === 'SEM_VINCULO_MAPA'
                                          ? 'Grupo sem vínculo MAPA: cadastre vínculos ou exclua as linhas antes de publicar.'
                                          : undefined
                                      }
                                      onClick={() => onPublishClick(item, mesN)}
                                    >
                                      <SendIcon />
                                      <span className={sl.className}>{sl.text}</span>
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuGroup>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                      </TableRow>
                      {isComercializacao && isExpanded && (
                        <TableRow>
                          <TableCell colSpan={activeColumns.length + 2}>
                            <div className="grid gap-4 py-2 md:grid-cols-2">
                              <div>
                                <p className="mb-2 text-sm font-semibold text-on-surface">Vendas</p>
                                <SubTabela
                                  rows={(item.detalhesVendas as unknown as Record<string, unknown>[]) || []}
                                  columns={COMERCIALIZACAO_DETAIL_COLUMNS}
                                  helpers={columnHelpers}
                                />
                              </div>
                              <div>
                                <p className="mb-2 text-sm font-semibold text-on-surface">Compras</p>
                                <SubTabela
                                  rows={(item.detalhesCompras as unknown as Record<string, unknown>[]) || []}
                                  columns={COMERCIALIZACAO_DETAIL_COLUMNS}
                                  helpers={columnHelpers}
                                />
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <div className="mt-4 flex items-center justify-center gap-4">
              <Button
                variant="outline"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Anterior
              </Button>
              <span className="text-sm text-on-surface-variant">
                {currentPage} / {totalPages}
              </span>
              <Button
                variant="outline"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Próximo
              </Button>
            </div>
          </>
        )}
      </div>

      <Dialog
        open={showModalDetalhes}
        onOpenChange={(open) => {
          if (!open) setShowModalDetalhes(false);
        }}
      >
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Produtos agregados ({modalDetalhes?.length || 0})</DialogTitle>
          </DialogHeader>
          {modalDetalhesLoading ? (
            <LoadingSpinner
              text="Carregando detalhes…"
              size="small"
            />
          ) : (
            <div className="max-h-[560px] overflow-auto overscroll-contain">
              <Table>
                <TableHeader>
                  <TableRow className="sticky top-0 z-10 bg-popover">
                    {modalDetailColumns.map((column) => (
                      <TableHead key={column.field}>{column.label}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {modalDetalhes && modalDetalhes.length > 0 ? (
                    <>
                      {modalDetalhes.map((detalhe, detIndex) => (
                        <TableRow key={(detalhe.id as string) || (detalhe.lote_id as string) || detIndex}>
                          {modalDetailColumns.map((column) => (
                            <TableCell key={`${detIndex}-${column.field}`}>{renderDetailValue(detalhe, column)}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted font-semibold">
                        {modalDetailColumns.map((column, colIndex) => (
                          <TableCell key={`total-${column.field}`}>{renderDetailTotalValue(column, colIndex)}</TableCell>
                        ))}
                      </TableRow>
                    </>
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={modalDetailColumns.length}
                        className="text-center text-on-surface-variant"
                      >
                        Nenhum detalhe encontrado.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={showModalHistorico}
        onOpenChange={(open) => {
          if (!open) setShowModalHistorico(false);
        }}
      >
        <DialogContent className="sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Histórico de versões</DialogTitle>
            {modalHistoricoItem && (
              <p className="text-sm text-on-surface-variant">
                {activeTransactionTab.label} ·{' '}
                {isProducaoHistorico ? formatarMesAno(modalHistoricoItem.mes) : formatarDataBR(modalHistoricoItem.data as string)} ·{' '}
                {(modalHistoricoItem.unidade_nome || modalHistoricoItem.nome_unidade) ?? '—'}
              </p>
            )}
          </DialogHeader>
          {modalHistoricoLoading ? (
            <LoadingSpinner
              text="Carregando histórico…"
              size="small"
            />
          ) : (
            <div className="max-h-[560px] overflow-auto overscroll-contain">
              <Table>
                <TableHeader>
                  <TableRow className="sticky top-0 z-10 bg-popover">
                    <TableHead>Status</TableHead>
                    {showOrigemColumn && <TableHead>Origem</TableHead>}
                    {isProducaoHistorico ? (
                      <TableHead>Total (kg)</TableHead>
                    ) : (
                      <>
                        <TableHead>Categoria / grupo</TableHead>
                        <TableHead>Total machos</TableHead>
                        <TableHead>Total fêmea</TableHead>
                        <TableHead>Peso total (kg)</TableHead>
                      </>
                    )}
                    <TableHead>Data publicação</TableHead>
                    <TableHead>Importado por</TableHead>
                    <TableHead>Importado em</TableHead>
                    <TableHead>Removido por</TableHead>
                    <TableHead>Removido em</TableHead>
                    <TableHead>Motivo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {modalHistorico.length > 0 ? (
                    modalHistorico.map((versao, index) => (
                      <TableRow
                        key={`${versao.id}-${index}`}
                        className={versao.ativo ? 'bg-success/10 font-medium' : undefined}
                      >
                        <TableCell>
                          {versao.ativo ? (
                            <span className="text-success">Ativo</span>
                          ) : (
                            <span className="text-on-surface-variant">Removido</span>
                          )}
                        </TableCell>
                        {showOrigemColumn && <TableCell>{versao.origem || '—'}</TableCell>}
                        {isProducaoHistorico ? (
                          <TableCell>{formatarNumeroBR(versao.total_mes)}</TableCell>
                        ) : (
                          <>
                            <TableCell>{versao.tipo || '—'}</TableCell>
                            <TableCell>{formatarNumeroInteiro(versao.total_mes)}</TableCell>
                            <TableCell>{formatarNumeroInteiro(versao.quantidade_femeas ?? 0)}</TableCell>
                            <TableCell>
                              {formatarNumeroBR((Number(versao.peso_machos) || 0) + (Number(versao.peso_femeas) || 0))}
                            </TableCell>
                          </>
                        )}
                        <TableCell>{versao.data_publicacao ? formatDateTime(versao.data_publicacao) : '—'}</TableCell>
                        <TableCell>{versao.importado_por || '—'}</TableCell>
                        <TableCell>{versao.importado_em ? formatDateTime(versao.importado_em) : '—'}</TableCell>
                        <TableCell>{versao.removido_por || '—'}</TableCell>
                        <TableCell>{versao.removido_em ? formatDateTime(versao.removido_em) : '—'}</TableCell>
                        <TableCell>{versao.motivo_exclusao || '—'}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={(isProducaoHistorico ? 8 : 11) + (showOrigemColumn ? 1 : 0)}
                        className="text-center text-on-surface-variant"
                      >
                        Nenhuma versão encontrada.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!publishConfirmCtx}
        onOpenChange={(open) => {
          if (!open) closePublishConfirmModal();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar publicação</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-on-surface-variant">Serão publicados apenas os dados disponíveis no período (expedição e/ou recebimento).</p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closePublishConfirmModal}
            >
              Cancelar
            </Button>
            <Button onClick={confirmPublish}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showModalNaoAbate}
        onOpenChange={(open) => {
          if (!open) closeNaoAbateModal();
        }}
      >
        <DialogContent
          ref={setNaoAbateComboboxPortalContainer}
          className="sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle>Publicar não Abate</DialogTitle>
          </DialogHeader>

          <form
            onSubmit={(e) => void handleNaoAbateSubmit(handleSaveNaoAbate)(e)}
            noValidate
          >
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="nao-abate-data">Data</FieldLabel>
                <Input
                  id="nao-abate-data"
                  type="date"
                  value={naoAbateData}
                  readOnly
                  disabled
                />
              </Field>

              <Field data-invalid={!!naoAbateErrors.unidade_id}>
                <FieldLabel htmlFor="nao-abate-unidade">Unidade *</FieldLabel>
                <Controller
                  control={naoAbateControl}
                  name="unidade_id"
                  render={({ field }) => (
                    <Combobox
                      items={naoAbateUnidades}
                      itemToStringLabel={(unidade: IUnidade) => unidade.nome || 'Sem nome'}
                      value={naoAbateUnidades.find((unidade) => String(unidade.id) === field.value) ?? null}
                      onValueChange={(unidade: IUnidade | null) => field.onChange(unidade ? String(unidade.id) : '')}
                      disabled={singleNaoAbateUnidadeLocked}
                    >
                      <ComboboxInput
                        id="nao-abate-unidade"
                        placeholder={naoAbateUnidadesLoading ? 'Carregando unidades…' : 'Selecione a unidade…'}
                        autoComplete="off"
                        aria-invalid={!!naoAbateErrors.unidade_id}
                        disabled={naoAbateUnidadesLoading || singleNaoAbateUnidadeLocked}
                      />
                      <ComboboxContent container={naoAbateComboboxPortalContainer}>
                        <ComboboxEmpty>Nenhuma unidade encontrada</ComboboxEmpty>
                        <ComboboxList>
                          {(unidade: IUnidade) => (
                            <ComboboxItem
                              key={unidade.id}
                              value={unidade}
                            >
                              {unidade.nome || 'Sem nome'}
                            </ComboboxItem>
                          )}
                        </ComboboxList>
                      </ComboboxContent>
                    </Combobox>
                  )}
                />
                {naoAbateUnidadesError && <FieldDescription className="text-destructive">Erro ao carregar unidades: {naoAbateUnidadesError}</FieldDescription>}
                <FieldError errors={naoAbateErrors.unidade_id ? [naoAbateErrors.unidade_id] : undefined} />
              </Field>

              <Field data-invalid={!!naoAbateErrors.justificativa}>
                <FieldLabel htmlFor="nao-abate-justificativa">Justificativa *</FieldLabel>
                <Input
                  id="nao-abate-justificativa"
                  type="text"
                  placeholder="Informe o motivo de não ter ocorrido abate neste dia…"
                  maxLength={500}
                  autoComplete="off"
                  aria-invalid={!!naoAbateErrors.justificativa}
                  {...registerNaoAbate('justificativa')}
                />
                <FieldError errors={naoAbateErrors.justificativa ? [naoAbateErrors.justificativa] : undefined} />
              </Field>
            </FieldGroup>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeNaoAbateModal}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={naoAbateMutation.isPending}
              >
                {naoAbateMutation.isPending ? (
                  <>
                    <Spinner data-icon="inline-start" />
                    Salvando…
                  </>
                ) : (
                  'Salvar'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
